/** @jsxImportSource theme-ui @jsxRuntime classic */

import { VisuallyHidden } from '@reach/visually-hidden';
import {
  animated,
  Controller,
  SpringValue,
  useSpring,
} from '@react-spring/web';
import { alpha } from '@theme-ui/color';
import {
  AnimationState,
  Box,
  Button,
  useAnimationStateSelector,
  useBreakpointIndex,
  usePageTransitionActions,
  usePageTransitionDispatch,
  useThemeUI,
} from '@tishman/components';
import * as Icons from '@tishman/icons';
import React, { useEffect, useLayoutEffect, useRef } from 'react';

const getTicketColumnWidth = (ticketCount: number) => {
  switch (ticketCount) {
    case 1:
      return `${30 / ticketCount}%`;
    case 2:
      return `${45 / ticketCount}%`;
    default:
      return `${60 / ticketCount}%`;
  }
};

export interface TicketComparisonModalProps {
  category:
    | Queries.BuyTicketsQuery['allSanityTicketCategory']['nodes'][0]
    | Queries.AttractionTorQuery['allSanityTicketCategory']['nodes'][0];
  onClose?: () => void;
}

export function TicketComparisonModal({
  category,
  onClose,
}: TicketComparisonModalProps): React.JSX.Element {
  const actions = usePageTransitionActions();
  const dispatch = usePageTransitionDispatch();
  const isMobile = useBreakpointIndex() < 2;
  const isTablet = useBreakpointIndex() === 2;
  const themeUI = useThemeUI();

  const ctrlIds = useRef(new Set<number>());

  const [transition, ref] = useSpring((_: SpringValue, ctrl: Controller) => {
    ctrlIds.current.add(ctrl.id);

    return {
      opacity: 0,
      y: 32,
    };
  });

  useEffect(() => {
    const ctrlIds_ = ctrlIds.current;

    ctrlIds_.forEach((animationId) => {
      dispatch(
        actions.add({
          animationState: AnimationState.SHOULD_ENTER,
          animationId,
        }),
      );
    });

    return () => {
      ctrlIds_.forEach((id) => {
        dispatch(actions.remove(id));
      });
    };
  }, [actions, dispatch]);

  const animationState = useAnimationStateSelector([...ctrlIds.current]);

  useLayoutEffect(() => {
    switch (animationState) {
      case AnimationState.SHOULD_ENTER: {
        ref.start({
          onStart: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.IS_ENTERING,
              }),
            );
          },
          onRest: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.DID_ENTER,
              }),
            );
          },
          opacity: 1,
          y: 0,
        });
        break;
      }
      case AnimationState.SHOULD_LEAVE: {
        ref.start({
          onStart: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.IS_LEAVING,
              }),
            );
          },
          onRest: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.DID_LEAVE,
              }),
            );
          },
          opacity: 0,
          y: 32,
        });
        break;
      }
    }
  }, [actions, animationState, dispatch, ref]);

  const ticketMinusCitypass = category.tickets?.filter(
    (ticket) => !ticket?.title?.includes('New York CityPASS'),
  );

  return (
    <animated.div
      style={{
        ...transition,
        backgroundColor: 'background',
        color: 'text',
        marginBottom: isMobile ? themeUI.theme.space[2] : 0,
        marginLeft: 'auto',
        marginRight: 'auto',
        maxHeight: isMobile
          ? `calc(100% - ${102 + themeUI.theme.space[2]}px)`
          : '80%',
        overflow: 'auto',
        padding: isMobile
          ? '0 16px 16px'
          : isTablet
          ? '0 30px 30px'
          : '0 50px 50px',
        placeSelf: 'center',
        position: 'relative',
        width: isMobile
          ? `calc(100% - ${(themeUI.theme.space[2] as number) * 2}px)`
          : `calc(100% - ${(themeUI.theme.space[4] as number) * 2}px)`,
      }}
    >
      <Box
        as="div"
        sx={{
          backgroundColor: 'background',
          position: 'sticky',
          top: 0,
          zIndex: 'overlay',
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            'background': 'transparent',
            'border': 'none',
            'fontSize': '0px',
            'padding': 3,
            'position': 'absolute',
            'right': (theme) => [
              `-${30 - (theme.space![2] as number)}px`,
              null,
              `-${50 - (theme.space![3] as number)}px`,
            ],
            'top': [2, null, 3],

            '&:hover': {
              background: 'transparent !important',
              svg: {
                transform: 'scale(1.1)',
              },
            },
          }}
        >
          <Icons.Close
            sx={{
              transition: 'transform 0.3s ease-in-out',
            }}
          />
        </Button>
      </Box>

      <table style={{ width: '100%' }}>
        <Box
          as="thead"
          sx={{ backgroundColor: 'background', position: 'sticky', top: 0 }}
        >
          <tr>
            <Box
              as="th"
              scope="col"
              sx={{
                color: ['background', null, 'text'],
                fontSize: [null, null, 4],
                fontWeight: [null, null, 'medium'],
                paddingBottom: [2, null, 5],
                paddingTop: ['60px', null, '80px'],
                paddingX: [1, null, 0],
                textAlign: 'left',
                textTransform: [null, null, 'uppercase'],
              }}
            >
              Compare Tickets
            </Box>

            {Array.isArray(ticketMinusCitypass) &&
              ticketMinusCitypass?.filter(Boolean).map((ticket) => (
                <Box
                  as="th"
                  key={ticket?.id}
                  scope="col"
                  sx={{
                    color: 'text',
                    fontSize: [1, null, 4],
                    fontWeight: 'medium',
                    paddingBottom: [3, null, 5],
                    paddingTop: ['60px', null, '80px'],
                    paddingX: [1, null, 0],
                    textTransform: [null, null, 'uppercase'],
                    width: getTicketColumnWidth(
                      ticketMinusCitypass?.length || 1,
                    ),
                  }}
                >
                  {ticket?.title}
                </Box>
              ))}
          </tr>
        </Box>
        <tbody>
          {category.availableFeatures?.map((feature) => (
            <tr key={feature}>
              <Box
                as="th"
                scope="row"
                sx={{
                  borderBottom: (theme) =>
                    `1px solid ${alpha('backgroundDisabled', 0.5)(theme)}`,
                  color: 'text',
                  fontSize: [1, null, 4],
                  fontWeight: 'normal',
                  paddingY: 2,
                  textAlign: 'left',
                }}
              >
                {feature}
              </Box>

              {Array.isArray(ticketMinusCitypass) &&
                ticketMinusCitypass?.filter(Boolean).map((ticket) => (
                  <Box
                    as="td"
                    key={ticket?.id}
                    sx={{
                      borderBottom: (theme) =>
                        `1px solid ${alpha('backgroundDisabled', 0.5)(theme)}`,
                      paddingY: 2,
                      textAlign: 'center',
                      verticalAlign: 'middle',
                    }}
                  >
                    {ticket?.includedFeatures?.includes(feature) && (
                      <Icons.Checkmark style={{ color: 'black' }} />
                    )}
                    <VisuallyHidden>
                      {ticket?.includedFeatures?.includes(feature)
                        ? 'Included'
                        : 'Not included'}
                    </VisuallyHidden>
                  </Box>
                ))}
            </tr>
          ))}
        </tbody>
      </table>
    </animated.div>
  );
}
